import type { DraftPayload } from "./dolt-client";
import type { Scenario } from "./types";
import type { Site } from "./platform-types";

export type DoltPersistenceConfig = {
  payload: DraftPayload | null;
  storageKey: string;
  baseRevision: number;
};

export type DoltLocalRecovery = {
  payload: DraftPayload;
  baseRevision: number;
};

type Scope = {
  storageKey: string;
  payload: DraftPayload | null;
  baseRevision: number;
};

let scope: Scope | null = null;

function record(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== "object" || Array.isArray(value))
    return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

/**
 * Reject values JSON.stringify would silently coerce (undefined array entries,
 * holes, NaN, Date, etc.). Undefined own properties of objects are allowed and
 * omitted, as with an optional field.
 */
function validJsonValue(value: unknown, seen = new WeakSet<object>()): boolean {
  if (value === null || typeof value === "string" || typeof value === "boolean")
    return true;
  if (typeof value === "number") return Number.isFinite(value);
  if (typeof value !== "object") return false;
  if (seen.has(value)) return false;
  seen.add(value);
  try {
    if (Array.isArray(value)) {
      for (let index = 0; index < value.length; index += 1) {
        if (!Object.prototype.hasOwnProperty.call(value, index)) return false;
        if (!validJsonValue(value[index], seen)) return false;
      }
      return true;
    }
    if (!record(value)) return false;
    if (Object.getOwnPropertySymbols(value).length > 0) return false;
    return Object.values(value).every(
      (item) => item === undefined || validJsonValue(item, seen),
    );
  } finally {
    seen.delete(value);
  }
}

function validRevision(value: unknown): value is number {
  return Number.isSafeInteger(value) && (value as number) >= 0;
}

function clone<T>(value: T): T {
  const encoded = JSON.stringify(value);
  if (encoded === undefined)
    throw new Error("Dolt persistence received non-JSON data");
  return JSON.parse(encoded) as T;
}

function validScenario(value: unknown): value is Scenario {
  if (!record(value)) return false;
  if (!validJsonValue(value)) return false;
  if (typeof value.id !== "string" || typeof value.name !== "string")
    return false;
  if (!record(value.assumptions) || !Array.isArray(value.buildings))
    return false;
  if (
    value.schemaVersion !== undefined &&
    (typeof value.schemaVersion !== "number" ||
      !Number.isSafeInteger(value.schemaVersion) ||
      value.schemaVersion > 1 ||
      value.schemaVersion < 0)
  ) {
    return false;
  }
  try {
    clone(value);
    return true;
  } catch {
    return false;
  }
}

function validSite(value: unknown): value is Site {
  return (
    validJsonValue(value) &&
    record(value) &&
    typeof value.id === "string" &&
    typeof value.name === "string"
  );
}

function validPayload(value: unknown): value is DraftPayload {
  if (
    !record(value) ||
    value.schemaVersion !== 1 ||
    !Array.isArray(value.scenarios) ||
    !Array.isArray(value.sites)
  )
    return false;
  if (value.activeId !== null && typeof value.activeId !== "string")
    return false;
  if (!value.scenarios.every(validScenario) || !value.sites.every(validSite))
    return false;
  return (
    value.activeId === null ||
    value.scenarios.some((scenario) => scenario.id === value.activeId)
  );
}

function requireScope(): Scope {
  if (!scope) throw new Error("Dolt persistence scope is not configured");
  return scope;
}

function storage(): Storage {
  return globalThis.localStorage;
}

function writeScope(next: Scope): boolean {
  try {
    storage().setItem(
      next.storageKey,
      JSON.stringify({
        payload: next.payload,
        baseRevision: next.baseRevision,
      }),
    );
    return true;
  } catch {
    return false;
  }
}

function readRecord(storageKey: string): DoltLocalRecovery | null {
  let raw: string | null;
  try {
    raw = storage().getItem(storageKey);
  } catch (error) {
    throw new Error(
      `Dolt local persistence unreadable for ${storageKey}: ${String(error)}`,
    );
  }
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new Error(
      `Corrupt Dolt local persistence record for ${storageKey}: not JSON`,
    );
  }
  if (
    !record(parsed) ||
    Object.keys(parsed).length !== 2 ||
    !Object.hasOwn(parsed, "payload") ||
    !Object.hasOwn(parsed, "baseRevision") ||
    !validRevision(parsed.baseRevision) ||
    !validPayload(parsed.payload)
  )
    throw new Error(
      `Corrupt Dolt local persistence record for ${storageKey}: delete the key to recover`,
    );
  return {
    payload: clone(parsed.payload),
    baseRevision: parsed.baseRevision,
  };
}

export function configureDoltPersistence(config: DoltPersistenceConfig): void {
  if (
    typeof config.storageKey !== "string" ||
    config.storageKey.length === 0 ||
    !validRevision(config.baseRevision)
  ) {
    throw new Error("Invalid Dolt persistence scope");
  }
  if (config.payload !== null && !validPayload(config.payload))
    throw new Error("Invalid Dolt persistence payload");
  scope = {
    storageKey: config.storageKey,
    payload: config.payload === null ? null : clone(config.payload),
    baseRevision: config.baseRevision,
  };
}

export function clearDoltPersistence(): void {
  scope = null;
}

export function readDoltPersisted(): DraftPayload | null {
  const current = requireScope().payload;
  return current === null ? null : clone(current);
}

export function readDoltSites(): Site[] {
  const current = requireScope().payload;
  return current === null ? [] : clone(current.sites as Site[]);
}

export function saveDoltLocal(
  scenarios: Scenario[],
  activeId: string | null,
): boolean {
  const current = requireScope();
  if (!Array.isArray(scenarios) || !scenarios.every(validScenario))
    throw new Error("Invalid Dolt scenarios");
  const sites = current.payload?.sites ?? [];
  const payload: DraftPayload = {
    schemaVersion: 1,
    scenarios: clone(scenarios),
    sites: clone(sites),
    activeId,
  };
  if (!validPayload(payload))
    throw new Error("Invalid Dolt persistence payload");
  current.payload = payload;
  return writeScope(current);
}

export function saveDoltSitesLocal(sites: Site[]): boolean {
  const current = requireScope();
  if (!Array.isArray(sites) || !sites.every(validSite))
    throw new Error("Invalid Dolt sites");
  const prior = current.payload;
  const payload: DraftPayload = {
    schemaVersion: 1,
    scenarios: prior?.scenarios ? clone(prior.scenarios) : [],
    sites: clone(sites),
    activeId: prior?.activeId ?? null,
  };
  current.payload = payload;
  return writeScope(current);
}

export function getDoltLocalRecovery(
  storageKey: string,
): DoltLocalRecovery | null {
  if (typeof storageKey !== "string" || storageKey.length === 0) return null;
  return readRecord(storageKey);
}

export function markDoltSaved(revision: number): void {
  const current = requireScope();
  if (!validRevision(revision)) throw new Error("Invalid Dolt revision");
  current.baseRevision = revision;
  writeScope(current);
}
